import React, { useEffect, useRef, useState } from "react";
import { Bot, ChevronDown, Check, Plus, Settings2 } from "lucide-react";
import { useAgents } from "../lib/agents-context";

/**
 * AgentSelector — dropdown no header do chat para escolher o agente
 * das PRÓXIMAS conversas. Dentro de uma conversa existente, o ChatPage
 * mostra apenas o badge do agente da conversa.
 */
export default function AgentSelector() {
    const { agents, selectedAgent, setSelectedAgentId, openAgentsDialog } = useAgents();
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const onClick = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, []);

    return (
        <div className="relative" ref={ref}>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                data-testid="agent-selector-btn"
                className={`flex items-center gap-1.5 rounded-full px-3 py-1 border transition-colors max-w-[180px] sm:max-w-[240px] ${
                    selectedAgent
                        ? "bg-primary/10 border-primary/40 text-primary"
                        : "bg-muted/40 border-border text-muted-foreground hover:text-foreground"
                }`}
                aria-label="Selecionar agente"
            >
                {selectedAgent?.avatar_url ? (
                    <span className="w-5 h-5 rounded-full overflow-hidden shrink-0 border border-primary/40 bg-[#111] flex items-center justify-center">
                        <img
                            src={selectedAgent.avatar_url}
                            alt=""
                            loading="lazy"
                            className="w-full h-full object-cover"
                            onError={(e) => { e.currentTarget.src = "/img/default-avatar.svg"; }}
                        />
                    </span>
                ) : (
                    <Bot className="w-3.5 h-3.5 shrink-0" strokeWidth={2.2} />
                )}
                <span className="text-[11px] md:text-xs font-semibold truncate">
                    {selectedAgent ? selectedAgent.name : "Sem agente"}
                </span>
                <ChevronDown className={`w-3 h-3 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>

            {open && (
                <div
                    className="absolute left-0 top-full mt-2 w-64 bg-[#0d0d0d] border border-border rounded-md shadow-2xl z-50 overflow-hidden"
                    data-testid="agent-selector-menu"
                >
                    <div className="px-3 py-2 text-[10px] font-mono uppercase tracking-[0.15em] text-muted-foreground border-b border-border">
                        Agente das novas conversas
                    </div>
                    <div className="max-h-56 overflow-y-auto py-1">
                        <MenuItem
                            active={!selectedAgent}
                            onClick={() => { setSelectedAgentId(null); setOpen(false); }}
                            testId="agent-option-none"
                        >
                            <span className="text-muted-foreground">Sem agente (padrão)</span>
                        </MenuItem>
                        {agents.map((a) => (
                            <MenuItem
                                key={a.id}
                                active={selectedAgent?.id === a.id}
                                onClick={() => { setSelectedAgentId(a.id); setOpen(false); }}
                                testId={`agent-option-${a.id}`}
                            >
                                <div className="flex items-center gap-2 min-w-0">
                                    <span className="w-6 h-6 rounded-full border border-border overflow-hidden bg-[#111] flex items-center justify-center shrink-0">
                                        {a.avatar_url ? (
                                            <img src={a.avatar_url} alt="" loading="lazy" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.src = "/img/default-avatar.svg"; }} />
                                        ) : (
                                            <Bot className="w-3.5 h-3.5 text-muted-foreground" strokeWidth={2} />
                                        )}
                                    </span>
                                    <div className="min-w-0">
                                        <div className="truncate font-medium">{a.name}</div>
                                        {a.skills?.length > 0 && (
                                            <div className="text-[10px] text-muted-foreground truncate">{a.skills.slice(0, 3).join(" · ")}</div>
                                        )}
                                    </div>
                                </div>
                            </MenuItem>
                        ))}
                    </div>
                    <div className="border-t border-border py-1">
                        <button
                            type="button"
                            onClick={() => { setOpen(false); openAgentsDialog("form"); }}
                            data-testid="agent-menu-create"
                            className="w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold text-primary hover:bg-primary/10 transition-colors"
                        >
                            <Plus className="w-3.5 h-3.5" strokeWidth={2.5} />
                            Criar novo agente
                        </button>
                        <button
                            type="button"
                            onClick={() => { setOpen(false); openAgentsDialog("list"); }}
                            data-testid="agent-menu-manage"
                            className="w-full flex items-center gap-2 px-3 py-2 text-xs text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                        >
                            <Settings2 className="w-3.5 h-3.5" />
                            Gerenciar meus agentes
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

function MenuItem({ children, active, onClick, testId }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testId}
            className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-xs text-left transition-colors ${
                active ? "bg-primary/10 text-foreground" : "text-foreground hover:bg-muted"
            }`}
        >
            <div className="min-w-0 flex-1">{children}</div>
            {active && <Check className="w-3.5 h-3.5 text-primary shrink-0" strokeWidth={2.5} />}
        </button>
    );
}
